import moment from 'moment'
import logger from '@adc/logger'
import seek, { seekTypes } from '@adc/seek'

const tautologicalExpressions = [
  {
    equals: {
      fields: 'event_configuration.web_live_status',
      values: [true],
    },
  },
  // TODO: In the past we have enabled these for internal @redcmail users. In the future my prefernce would be to
  // enable via something like `fe.resi.enable-test-mode` and have test data be present in all apps if not disabled
  // specifially based on request context
  {
    equals: {
      fields: 'event_configuration.test_event',
      values: [false],
    },
  },
  {
    range: {
      field: 'listing_count',
      gt: 0,
    },
  },
]

const calculateExpressionsFromParams = params => {
  const expressions = [...tautologicalExpressions]

  // NOTE: Do not trust lower environment data. Just don't do it. The system
  // doesn't have the constraints in the code that says when data is incorrect
  // but in practice production data is only ever configured a certain way by
  // the operations folks who make sure the events and venues are correct.
  //
  // In this case, even though you know, and I know, that for in-person
  // single-day auctions the end date should equal the initial date because it
  // happens at a specific physical venue for one day only (OH online is
  // a whole other thing) the system doesn't model the data that way. The venue
  // has the start data and that's it. At the listing level we can use
  // `listing.auction_date` if it's in-person trustee but we aren't dealing
  // with listings here. Ideally the schedules should not be dependent on
  // product type at all. There is a start and the end, and they can be the
  // same for certain auctions we run. That makes sense in my brain but the
  // isn't the reality of the platform as of right now.
  //
  // NOTE: On my local machine for prod data this results in my cpu spinning.
  // The seek call is optimized with explicit fields down as you can see in
  // both providers and I've tested against the prod index for that and it's
  // fast so it's definetely in the schema resolution and validation in the
  // graph-api. The kill switch is just in case we test in qa but the data is
  // not representative of production at all, in which case we can remotely
  // turn this feature off until we can properly benchmark a solution.
  //
  if (params.hide_past_events) {
    expressions.push({
      or: {
        expressions: [
          {
            range: {
              field: 'venues.venue_schedule.initial_bidding_end_local_date',
              gte: 'now/d',
            },
          },
          {
            range: {
              field: 'venues.venue_schedule.bid_start_local_date',
              gte: 'now/d',
            },
          },
        ],
      },
    })

    //
    // NOTE:  This one feels more semantically correct but the performance is horrible because of what it needs to do.
    // expressions.push({
    //   or: {
    //     expressions: [
    //       {
    //         and: {
    //           expressions: [
    //             {
    //               equals: {
    //                 fields: 'trustee_sale',
    //                 values: [false],
    //               },
    //             },
    //             {
    //               range: {
    //                 field: 'venues.venue_schedule.initial_bidding_end_local_date',
    //                 gte: 'now/d',
    //               },
    //             },
    //           ]
    //         }
    //       },
    //       {
    //         and: {
    //           expressions: [
    //             {
    //               equals: {
    //                 fields: 'trustee_sale',
    //                 values: [true],
    //               },
    //             },
    //             {
    //               range: {
    //                 field: 'venues.venue_schedule.bid_start_local_date',
    //                 gte: 'now/d',
    //               },
    //             },
    //           ]
    //         }
    //       },
    //     ],
    //   },
    // })
  }

  // NOTE: This is a weird by-product of the routing in this app. Since it goes /:type/:state it means we also need a value
  // for :type to make the route unambiguous. So we can't put a default null or empty string value there, but we shouldn't be adding a filter
  // unless there is something to be filtered on.
  if (params.product_type && params.product_type !== 'all') {
    expressions.push({
      equals: {
        fields: 'trustee_sale',
        values: [params.product_type === 'trustee'],
      },
    })
  }

  if (params.buying_type) {
    if (params.buying_type === 'direct_offer_enabled') {
      expressions.push({
        equals: {
          fields: 'event_configuration.is_offer_enabled',
          values: [true],
        },
      })
    } else {
      expressions.push({
        equals: {
          fields: 'event_type.keyword',
          values: [params.buying_type],
        },
      })
    }
  }

  // NOTE: Not using `event.states `because none of the data of the prod index seems to have it even if `web_live_status` is
  // enabled. Checked with backend and this is how we match for venue scheduling also so it works the same.
  if (params.state) {
    expressions.push({
      or: {
        expressions: [
          {
            equals: {
              fields:
                'venues.venue_address.country_primary_subdivision.keyword',
              values: [params.state],
            },
          },
          {
            match: {
              field: 'venues.venue_configuration.online_state_codes',
              value: params.state,
            },
          },
        ],
      },
    })
  }

  // NOTE: Not using `event.event_schedule` this because none of the data of the index seems to have this at all. It's
  // all venue level schedules we need to match on.
  //
  // NOTE: This is called `bid_end_date` but it's really something like 'valid_auction_day' or something like that. The
  // meaning of this filter was modified during repipe to better reflect how people use it.
  //
  // TODO: Change this param name once UAT accepts
  if (params.bid_end_date) {
    const date = moment(params.bid_end_date, 'YYYY-MM-DD').format(
      moment.HTML5_FMT.DATE
    )

    expressions.push({
      or: {
        expressions: [
          {
            and: {
              expressions: [
                {
                  equals: {
                    fields: 'trustee_sale',
                    values: [false],
                  },
                },
                {
                  range: {
                    field:
                      'venues.venue_schedule.initial_bidding_end_local_date',
                    gte: date,
                  },
                },
                {
                  range: {
                    field: 'venues.venue_schedule.bid_start_local_date',
                    lte: date,
                  },
                },
              ],
            },
          },
          {
            and: {
              expressions: [
                {
                  equals: {
                    fields: 'trustee_sale',
                    values: [true],
                  },
                },
                {
                  equals: {
                    fields: 'venues.venue_schedule.bid_start_local_date',
                    values: [date],
                  },
                },
              ],
            },
          },
        ],
      },
    })
  }

  return expressions
}

const fetchMorpheusEvents = ({ limit, offset, ...params }) => {
  const calculatedExpressions = calculateExpressionsFromParams(params)

  logger.debug('Search expressions', calculatedExpressions)

  return seek
    .for(seekTypes.EVENTS)
    .operationName('resiCalendar_fetchEvents')
    .offset(offset || 0)
    .size(limit)
    .fields([
      'event_id',
      'event_code',
      'event_type',
      'title',
      'trustee_sale',
      'listing_count',
      'event_configuration.present_as_online_event',
      'event_configuration.is_offer_enabled',
      'venues.venue_id',
      'venues.listing_count',
      'venues.venue_schedule.bid_start_local_date',
      'venues.venue_schedule.initial_bidding_end_local_date',
      'venues.venue_address.country_primary_subdivision',
    ])
    .sortByField('event_schedule.campaign_start_date', 'ASC')
    .sortByField('event_id', 'ASC')
    .dsl({
      and: {
        expressions: calculatedExpressions,
      },
    })
    .execute()
    .then(({ data }) => ({ ...data.seek, offset }))
}

const EventsProvider = {
  fetchEvents: fetchMorpheusEvents,
}

export default EventsProvider
export { calculateExpressionsFromParams, tautologicalExpressions }
